import { io, type Socket } from "socket.io-client";
import { getAccessToken } from "@/auth/keycloak.js";

/**
 * The app's single `/cable` connection (ADR-026).
 *
 * **One socket per tab, shared by every feature.** Rooms — not connections — are how subscriptions are separated, so a
 * facility list, an open editor and (later) the header roster all multiplex over the same link. Opening a socket per
 * feature would multiply handshakes, and each one re-verifies a JWT server-side.
 *
 * Auth is the `auth.token` payload, matching the gateway. Socket.IO re-sends it on every reconnect, and the callback
 * form means a **fresh** token is fetched each time — necessary because the server disconnects a socket once its JWT
 * expires (a socket must not outlive its token), and the reconnect must not replay the dead one.
 */
let socket: Socket | null = null;

export function cable(): Socket {
  if (socket) return socket;
  socket = io({
    path: "/cable",
    // Same-origin: the tenant's own host, which is what the server's origin allow-list expects.
    withCredentials: true,
    // The callback runs per (re)connect, so an expired token is never replayed.
    auth: (cb: (data: Record<string, unknown>) => void) => {
      void getAccessToken().then((token) => cb({ token: token ?? "" }));
    },
    // Start on websocket; polling remains the fallback for networks that block upgrades.
    transports: ["websocket", "polling"],
  });

  // **Reconnect after a SERVER-initiated close (diff-panel R7-F3).**
  //
  // The gateway disconnects a socket once its JWT expires — correct, and the whole point of the expiry sweep. But
  // Socket.IO does NOT auto-reconnect from `io server disconnect`: it assumes the server meant it. So at the first
  // token expiry the socket closed and stayed closed, and presence plus every live lock update silently stopped until
  // a full page reload. Lock correctness was never at risk (HTTP stays the authority) — but the realtime layer quietly
  // died mid-session, which is worse than never having it, because the UI still looks live.
  //
  // Reconnecting explicitly re-runs the `auth` callback above, which fetches a FRESH token, and every `joinRoom` caller
  // re-subscribes on the resulting `connect`.
  socket.on("disconnect", (reason) => {
    if (reason === "io server disconnect") socket?.connect();
  });
  return socket;
}

/**
 * Join a room for as long as the caller is mounted, and leave on cleanup.
 *
 * Returns an unsubscribe function shaped for a React effect. Re-subscribes on every reconnect, because server-side room
 * membership lives on the socket: a dropped connection silently forgets every room, and without the `connect` handler a
 * reconnected client would sit there receiving nothing while appearing fine.
 */
export function joinRoom(event: { subscribe: string; unsubscribe: string }, payload: Record<string, number>): () => void {
  const s = cable();
  const join = (): void => {
    s.emit(event.subscribe, payload);
  };
  if (s.connected) join();
  s.on("connect", join);
  return () => {
    s.off("connect", join);
    if (s.connected) s.emit(event.unsubscribe, payload);
  };
}
